import { useQuery } from "@apollo/client";
import moment from "moment";
import {
  GetProfileOverviewQueryQuery,
  GetProfileOverviewQueryQueryVariables,
} from "types/graphql-schema";

import Avatar from "@components/avatar/avatar";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import { getUrlUsingTypename } from "@helpers/helpers";

import getProfileOverviewQuery from "./graphql/get-profile-overview-query";
import ProfileActivities from "./overview-tab/components/profile-activities";
import ProfileArtifacts from "./overview-tab/components/profile-artifacts";
import ProfileCoachingInsights from "./overview-tab/components/profile-coaching-insights";
import ProfileLastOneonone from "./overview-tab/components/profile-last-oneonone";
import ProfileEmptyState from "./profile-empty-state";

const ProfileOverview = ({ profileId }: { profileId: number }) => {
  const startOfHour = moment().startOf("hour").format();
  const { data, loading } = useQuery<
    GetProfileOverviewQueryQuery,
    GetProfileOverviewQueryQueryVariables
  >(getProfileOverviewQuery, {
    variables: {
      userId: profileId || -1,
      nextOneononeAfterDate: startOfHour,
      lastOneononeBeforeDate: startOfHour,
    },
    skip: !profileId,
  });
  const profile = data?.user;
  const roleStr =
    profile?.currentRoles.map((role) => role.title).join(", ") || "";

  return (
    <div>
      {loading ? (
        <Loading>Loading profile...</Loading>
      ) : !profile ? (
        <div>
          <ProfileEmptyState>Profile not found.</ProfileEmptyState>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-4">
            <Avatar
              user={profile}
              size={14}
              className="h-[56px] w-[56px] rounded-md"
            />
            <div>
              <div className="font-medium text-gray-900 text-lg">
                <AppLink to={getUrlUsingTypename(profile)}>
                  {profile.name}
                </AppLink>
              </div>
              {roleStr && (
                <div className="text-gray-500 text-sm tracking-tight">
                  {roleStr}
                </div>
              )}
            </div>
          </div>

          {waffle.flag_is_active("coaching-insights") && (
            <ProfileCoachingInsights profile={profile} />
          )}
          <ProfileLastOneonone data={data} />
          <ProfileArtifacts profile={profile} />

          <ProfileActivities profileId={profile.id} />
        </div>
      )}
    </div>
  );
};

export default ProfileOverview;
